import { get, isEmpty, nth } from "@s-libs/micro-dash";

import { User } from "@/types/models/User.interface";
import processUserFeatures from "@/users/v3/useGetUserProperties/helpers/processUserFeatures/processUserFeatures";
import { UserProperties } from "@/types/models/User/v3";
import getAllLicenses from "../getAllLicenses";

export interface GetUserPropertiesOptions {
  shouldUseNewUserApi: boolean;
}

/**
 * Returns an object with properties that describe the user's role, license state, and other
 * @deprecated Please use `useGetUserProperties` instead
 * @param {User} user - The user object to get properties for
 * @param options
 */
function getUserProperties(
  user: User,
  options?: GetUserPropertiesOptions,
): UserProperties {
  if (options?.shouldUseNewUserApi) {
    throw new Error(
      "getUserProperties is deprecated. Please use useGetUserProperties instead",
    );
  }

  const getFeatureState = (name: string) =>
    get(user, ["features", name]) || "off";

  const hasFeature = (name: string) => getFeatureState(name) === "on";

  const getActiveLicense = (product: string) => {
    if (!user) return;
    return nth(getAllLicenses(user, { product, onlyActive: true }), 0);
  };

  // User roles
  const isAuthenticatedUser = !!user?.is_authenticated;
  const isTeacher = user?.teacher?.role === "teacher";
  const isParent = user?.teacher?.role === "parent";
  const isStaff = user?.is_staff;
  const isStudent = user?.student?.id !== undefined;
  const hasTeacherRole = user?.roles?.some((r) => r === "teacher");

  // Admin roles
  const isAuthorizedDistrictAdmin =
    !!user?.district_admin?.date_accepted_terms_of_use;
  const isAuthorizedPrincipal =
    !!user?.principal && user?.principal?.validation_state === "valid";
  const isAuthorizedTeacher = user?.teacher?.validation_state === "valid"; // TODO - Check how to adapt this to V3
  const isAdmin = isAuthorizedPrincipal || isAuthorizedDistrictAdmin;

  // License states
  const activeProLicense = getActiveLicense("pro");
  const activeEssentialsLicense = !!getActiveLicense("essentials");
  const activeELALicense = !!getActiveLicense("ela");
  const activeScienceLicense = !!getActiveLicense("science");
  const activeSSLicense = !!getActiveLicense("social_studies");
  const activeStemLicense = !!getActiveLicense("stem");
  const isTrialUser = activeProLicense?.is_trial || false;
  const isFreeUser = isEmpty(getAllLicenses(user, {}));

  const hasActiveProductLicense = [
    activeEssentialsLicense,
    activeELALicense,
    activeScienceLicense,
    activeSSLicense,
    activeStemLicense,
  ].some((isActive) => !!isActive);

  const features = user && user?.features ? Object.keys(user?.features) : [];

  const featureBasedProps = processUserFeatures(hasFeature, features);

  const isLiteStudent = Boolean(isStudent && featureBasedProps.isLiteUser);

  return <UserProperties>{
    ...featureBasedProps,
    activeELALicense,
    activeEssentialsLicense,
    activeSSLicense,
    activeScienceLicense,
    activeStemLicense,
    hasActiveProductLicense,
    hasFeature,
    hasTeacherRole,
    isAdmin,
    isAuthenticatedUser,
    isAuthorizedDistrictAdmin,
    isAuthorizedPrincipal,
    isAuthorizedTeacher,
    isFreeUser,
    isLiteStudent,
    isParent,
    isStaff,
    isStudent,
    isTeacher,
    isTrialUser,
  };
}

export default getUserProperties;
